import { useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import InfoButton, { type ScoringPoints } from './InfoButton';
import {
  useLeaderboard,
  type LeaderboardEntry,
  type LeaderboardPeriod,
} from '@/api/useLeaderboard';
import { RoundPrize } from '@/api/lobby.types';

/*
  Sheet height. The extra term adds nothing below an 800px viewport — the SE
  keeps its current 173px — then takes a third of whatever height a taller
  phone has beyond that, which is the empty grass under the squad.
*/
export const SHEET_HEIGHT = 'clamp(170px, calc(26vh + max(0px, (100vh - 800px) * 0.55)), 300px)';
const SHEET_EXPANDED_HEIGHT = '75vh';
const SWIPE_THRESHOLD = 30;

/*
  Badge colour follows the prize tier a rank falls into, not the rank itself.
  The operator decides which positions win, so a fixed podium would mislabel any
  configuration that is not top-three — a rank inside tier 4 is in the money and
  needs to say so. Tiers past the third share one colour for now.
*/
const TIER_COLORS: Record<number, string> = {
  1: '#E8C248',
  2: '#C6CBD4',
  3: '#CD7F32',
};
const QUALIFYING_COLOR = '#3CBE1E';

interface BottomSheetProps {
  /** Round whose leaderboard the sheet shows; null while the lobby resolves. */
  roundId: string | null;
  /** The month this round counts towards; null hides the period toggle. */
  month?: { id: string; name: string; prizes: RoundPrize[] } | null;
  ariaLabel?: string;
  children?: ReactNode;
  /** The round's prize bands; a rank inside one gets a badge. */
  prizes?: RoundPrize[];
  /**
   * The toggle is owned by the lobby so the prize line above the sheet shows
   * the same period's rewards as the board below it.
   */
  period: LeaderboardPeriod;
  onPeriodChange: (period: LeaderboardPeriod) => void;
  points: ScoringPoints;
}

/**
 * Persistent bottom sheet, fixed at one height. It never dismisses, so there
 * is no scrim and no close affordance; the list scrolls within the sheet.
 */
export default function BottomSheet({
  roundId,
  ariaLabel,
  children,
  prizes = [],
  month = null,
  period,
  onPeriodChange,
  points,
}: BottomSheetProps) {
  const { t } = useTranslation();
  const activeId = period === 'week' ? roundId : (month?.id ?? null);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useLeaderboard(
    period,
    activeId
  );

  const [expanded, setExpanded] = useState(false);

  // Each period badges against its own tiers; the two sets are configured
  // separately and a rank that wins weekly need not win monthly.
  const activePrizes = period === 'week' ? prizes : (month?.prizes ?? []);

  const pages = data?.pages ?? [];
  const me = pages[0]?.me ?? null;
  const entries = pages.flatMap(page => page.entries);

  // Your row is pinned above, so it is excluded here rather than shown twice.
  const rows = entries.filter(e => e.userId !== me?.userId);
  // Ranks materialise at scoring time; until then fall back to list position
  // (the backend orders pages to match the eventual rank). Pages can start
  // past the first once older ones drop off, so the offset comes from each
  // page, not the flat index. A pinned row from beyond the loaded pages has no
  // position to fall back to.
  const positionOf = new Map(
    pages.flatMap(page =>
      page.entries.map((row, i) => [row.userId, (page.page - 1) * page.limit + i + 1] as const)
    )
  );
  const rankOf = (row: LeaderboardEntry) => row.rank ?? positionOf.get(row.userId) ?? '—';

  /** Badge colour for a rank, or undefined when that position wins nothing. */
  const badgeFor = (rank: number | string) => {
    if (typeof rank !== 'number') return undefined;
    const tier = activePrizes.find(p => rank >= p.rankFrom && rank <= p.rankTo);
    return tier ? (TIER_COLORS[tier.tier] ?? QUALIFYING_COLOR) : undefined;
  };

  const myRank = me ? rankOf(me) : null;
  const myBadge = myRank === null ? undefined : badgeFor(myRank);

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 w-full touch-pan-y bg-[#222429] border-t border-[#2E3441] text-white flex flex-col transition-[height] duration-300"
      style={{
        height: expanded ? SHEET_EXPANDED_HEIGHT : SHEET_HEIGHT,
        boxShadow: '0px 5px 11.2px 0px #00000075 inset',
      }}
      aria-label={ariaLabel ?? t('lobby.squadDetails')}
    >
      {/*
        The button is absolutely placed rather than a flex sibling so the title
        stays centred on the sheet, not on the space left over beside it.
      */}
      <motion.div
        className="shrink-0 relative flex items-center justify-center pt-[12px] pb-[4px] touch-none"
        onPanEnd={(_, info) => {
          if (info.offset.y < -SWIPE_THRESHOLD) setExpanded(true);
          else if (info.offset.y > SWIPE_THRESHOLD) setExpanded(false);
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded(open => !open)}
          aria-expanded={expanded}
          className="flex items-center gap-[6px] font-poppins font-semibold text-[16px] leading-none uppercase"
        >
          {t('sheet.leaderboard')}
          <svg
            aria-hidden
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
          >
            <path d="M1 5l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <div className="absolute right-[4px] top-[10px] -translate-y-[12px]">
          <InfoButton points={points} />
        </div>
      </motion.div>
      {month && (
        <div className="shrink-0 mx-[12px] mb-[6px] flex rounded-[8px] bg-[#121212] p-[3px]">
          {(['week', 'month'] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => onPeriodChange(p)}
              aria-pressed={period === p}
              className={`flex-1 rounded-[6px] py-[5px] font-poppins text-[12px] font-semibold transition-colors ${
                period === p ? 'bg-white text-black' : 'text-[#898E9A]'
              }`}
            >
              {p === 'week' ? t('sheet.weekly') : t('sheet.monthly')}
            </button>
          ))}
        </div>
      )}

      {/*
        Column headers and your row both sit outside the scroll area, so they
        stay put while the board moves underneath. Their widths are repeated
        from the table's colgroup — the cost of taking them out of the markup.
      */}
      <div className="shrink-0 flex items-center text-[12px] font-medium leading-none text-[#898E9A]">
        <span className="w-[56px] shrink-0 text-center py-[6px]">{t('sheet.rank')}</span>
        <span className="flex-1 text-left py-[6px]">{t('sheet.user')}</span>
        <span className="w-[44px] shrink-0 text-center py-[6px]">{t('sheet.shots')}</span>
        <span className="w-[44px] shrink-0 text-center py-[6px]">{t('sheet.goals')}</span>
        <span className="w-[44px] shrink-0 text-center py-[6px]">{t('sheet.pts')}</span>
      </div>
      {me && (
        <div
          className="shrink-0 flex items-center text-[12px] font-semibold text-white"
          style={{ background: '#E02125' }}
        >
          <span className="w-[56px] shrink-0 text-center py-[10px]">
            {myBadge ? (
              <span
                className="inline-flex min-w-[26px] items-center justify-center rounded-[4px] px-[6px] py-[2px] text-black"
                style={{ background: myBadge }}
              >
                {myRank}
              </span>
            ) : (
              myRank
            )}
          </span>
          <span className="flex-1 text-left py-[10px]">{t('sheet.you')}</span>
          <span className="w-[44px] shrink-0 text-center py-[10px]">{me.shotCount}</span>
          <span className="w-[44px] shrink-0 text-center py-[10px]">{me.goalCount}</span>
          <span className="w-[44px] shrink-0 text-center py-[10px]">{me.score}</span>
        </div>
      )}

      <div
        className="flex-1 overflow-y-auto pt-1 pb-[calc(env(safe-area-inset-bottom,0)+24px)]"
        onScroll={event => {
          const el = event.currentTarget;
          const nearEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
          if (nearEnd && hasNextPage && !isFetchingNextPage) fetchNextPage();
        }}
      >
        <table className="w-full table-fixed border-collapse font-poppins">
          <colgroup>
            <col className="w-[56px]" />
            <col />
            <col className="w-[44px]" />
            <col className="w-[44px]" />
            <col className="w-[44px]" />
          </colgroup>
          <thead className="sr-only">
            <tr className="text-[12px] font-medium leading-none text-[#898E9A]">
              <th scope="col" className="w-[56px] text-center font-medium py-[6px]">
                Rank
              </th>
              <th scope="col" className="text-left font-medium py-[6px]">
                User
              </th>
              <th scope="col" className="w-[44px] text-center font-medium py-[6px]">
                Shots
              </th>
              <th scope="col" className="w-[44px] text-center font-medium py-[6px]">
                Goals
              </th>
              <th scope="col" className="w-[44px] text-center font-medium py-[6px]">
                Pts
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const rank = rankOf(row);
              const badge = badgeFor(rank);
              return (
                <tr
                  key={row.userId}
                  className="bg-[#121212] border-b border-[#2A2D33] text-[12px] font-semibold text-white"
                >
                  <td className="text-center py-[10px]">
                    {badge ? (
                      <span
                        className="inline-flex min-w-[26px] items-center justify-center rounded-[4px] px-[6px] py-[2px] text-black"
                        style={{ background: badge }}
                      >
                        {rank}
                      </span>
                    ) : (
                      rank
                    )}
                  </td>
                  <td className="text-left py-[10px]">{row.username ?? 'Player'}</td>
                  <td className="text-center py-[10px]">{row.shotCount}</td>
                  <td className="text-center py-[10px]">{row.goalCount}</td>
                  <td className="text-center py-[10px]">{row.score}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!isLoading && rows.length === 0 && (
          <p className="pt-6 text-center font-poppins text-[12px] font-medium text-[#898E9A]">
            {t('sheet.empty')}
          </p>
        )}
        {children}
      </div>
    </div>
  );
}
